import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../PageHeader';
import { LOGO_SIZE } from '../../ui/Logo';
import PageMainContent from '../PageMainContent';
import { useTranslation } from 'react-i18next';
import PageBottomContent from '../PageBottomContent';
import { APP_ROUTES } from '../../app.config.vars/routes';
import { generateTaskRoutePath } from '../../helpers';
import Loader from '../../ui/Loader';
import { getAdminRally } from '../../api/admin/rallies';
import { getAdminTasks } from '../../api/admin/tasks';
import { setIsModalOpen, setModalContent } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
import { useAppDispatch } from '../../store';
import Headline from '../../ui/Headline';
import AdminPreviewGameTask from '../../modules/AdminPreviewGameTask';
import { useValidator, } from '../../hooks/formValidator/useValidator';
import { isAnswerSelected, isFileUploaded, isNotEmpty } from '../../hooks/formValidator/validators';
import AdminPreviewGameplayControls from '../../modules/AdminPreviewGameControls/gameplayControls';
import AdminPreviewGameNavBar from '../../components/AdminPreviewGameNavBar';
const inputTaskAnswerValidators = {
    inputTaskAnswer: [isNotEmpty],
};
const selectionTaskAnswerValidators = {
    selectionTaskAnswer: [isAnswerSelected],
    photoUploadTaskAnswer: [isFileUploaded],
};
const AdminPreviewMode = ({ previewType, rallyId, currentTaskNumber, }) => {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    const dispatch = useAppDispatch();
    const [rally, setRally] = useState(null);
    const [tasks, setTasks] = useState(null);
    const [currentTaskInfo, setCurrentTaskInfo] = useState(null);
    const [currentTaskStatus, setCurrentTaskStatus] = useState(null);
    const [showTaskResult, setShowTaskResult] = useState(false);
    const [currentSelectionTaskAnswers, setCurrentSelectionTaskAnswers] = useState(null);
    const [currentInputTaskAnswer, setCurrentInputTaskAnswer] = useState('');
    const [currentPhotoUploadTaskAnswer, setCurrentPhotoUploadTaskAnswer] = useState(null);
    const [currentValidatedInputTaskAttempt, setCurrentValidatedInputTaskAttempt] = useState(null);
    const { formErrors: taskAnswerValidationErrors, validateInputField, validateSelect, clearErrors, } = useValidator({
        inputSchema: inputTaskAnswerValidators,
        selectSchema: selectionTaskAnswerValidators,
    });
    const fetchData = async () => {
        try {
            const rally = await getAdminRally(rallyId);
            const tasks = await getAdminTasks(rallyId);
            setRally(rally);
            setTasks(tasks);
        }
        catch (error) {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
    };
    useEffect(() => {
        fetchData();
    }, [rallyId, i18n.language]);
    useEffect(() => {
        if (currentTaskNumber && +currentTaskNumber) {
            if (tasks?.length) {
                const currentTaskInfo = tasks.find((task) => task.sorting === +currentTaskNumber);
                if (currentTaskInfo) {
                    setCurrentTaskInfo(currentTaskInfo);
                    clearErrors();
                }
                if (currentTaskInfo?.taskType === 'action' &&
                    (currentTaskInfo?.answerType === 'single-selection' ||
                        currentTaskInfo?.answerType === 'multiple-selection')) {
                    setCurrentSelectionTaskAnswers(currentTaskInfo.answers.map((answer) => ({
                        uid: answer.uid,
                        content: answer.content[i18n.language],
                        isSelected: false,
                    })));
                }
                if (currentTaskInfo?.taskType === 'action' &&
                    currentTaskInfo?.answerType === 'player-selection') {
                    const playersPlaceholder = Array.from({ length: rally?.isTeamRally ? 3 : 1 }, (_, index) => ({
                        uid: index,
                        content: `${t('placeholder.player_name')} ${index + 1}`,
                        isSelected: false,
                    }));
                    setCurrentSelectionTaskAnswers(playersPlaceholder);
                }
                else if (currentTaskInfo?.taskType === 'action' &&
                    (currentTaskInfo?.answerType === 'text' ||
                        currentTaskInfo?.answerType === 'validated-text')) {
                    setCurrentInputTaskAnswer('');
                }
                else if (currentTaskInfo?.taskType === 'action' &&
                    currentTaskInfo?.answerType === 'upload') {
                    setCurrentPhotoUploadTaskAnswer(null);
                }
            }
        }
    }, [rally, tasks, currentTaskNumber, i18n.language]);
    useEffect(() => {
        setCurrentValidatedInputTaskAttempt(1);
    }, [currentTaskNumber]);
    const handleNavButtonClick = (chosenTaskNumber) => {
        setShowTaskResult(false);
        navigate(generateTaskRoutePath(APP_ROUTES.adminRallyPreview, rallyId, chosenTaskNumber));
    };
    const handleSelectionTaskAnswerButtonClick = (answerId) => {
        switch (currentTaskInfo.answerType) {
            case 'single-selection':
            case 'player-selection':
                setCurrentSelectionTaskAnswers(currentSelectionTaskAnswers.map((answer) => ({
                    ...answer,
                    isSelected: answer.uid === answerId,
                })));
                break;
            case 'multiple-selection':
                const currentAnswer = currentSelectionTaskAnswers?.find((answer) => answer.uid === answerId);
                if (currentAnswer) {
                    if (currentAnswer.isSelected) {
                        if (currentSelectionTaskAnswers
                            .filter((answer) => answer.uid !== answerId)
                            .find((answer) => answer.isSelected)) {
                            setCurrentSelectionTaskAnswers(currentSelectionTaskAnswers.map((answer) => {
                                return answer.uid !== answerId ? answer : { ...answer, isSelected: false };
                            }));
                        }
                    }
                    else {
                        setCurrentSelectionTaskAnswers(currentSelectionTaskAnswers.map((answer) => {
                            return answer.uid !== answerId ? answer : { ...answer, isSelected: true };
                        }));
                    }
                }
                break;
        }
    };
    const handleInputTaskAnswerChange = (event) => setCurrentInputTaskAnswer(event.currentTarget.value);
    const handlePhotoUploadTaskAnswerChange = (file) => {
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                try {
                    const result = reader.result;
                    if (typeof result === 'string') {
                        setCurrentPhotoUploadTaskAnswer(result);
                    }
                }
                catch (error) {
                    console.log(error);
                }
            };
            reader.readAsDataURL(file);
        }
        else {
            setCurrentPhotoUploadTaskAnswer(file);
        }
    };
    const goToNextTask = () => {
        const nextAvailableTask = tasks.find((task) => task.sorting > +currentTaskNumber);
        if (nextAvailableTask) {
            navigate(generateTaskRoutePath(APP_ROUTES.adminRallyPreview, rallyId, nextAvailableTask.sorting));
        }
        else {
            // TODO exit preview
        }
    };
    const handleTaskTryAgainButtonClick = async () => {
        setShowTaskResult(false);
        setCurrentValidatedInputTaskAttempt((currentAttempt) => currentAttempt ? currentAttempt + 1 : null);
    };
    const handleTargetLocationCheckedIn = async () => {
        setCurrentTaskStatus('completed');
        setShowTaskResult(true);
    };
    return (_jsx("div", { className: 'page-content-wrapper rally-game-page', children: rally && tasks && currentTaskInfo ? (_jsxs(_Fragment, { children: [_jsx(PageHeader, { logoSize: LOGO_SIZE.SMALL, logoLink: '/', title: _jsx(Headline, { title: rally.title[i18n.language], type: 'h1', layout: 4, spaceBottom: false }) }), _jsx(PageMainContent, { children: _jsxs("div", { className: "rally-game-page-container", children: [_jsx(AdminPreviewGameNavBar, { disable: previewType === 'single-task', onNavButtonClick: previewType === 'rally' ? handleNavButtonClick : undefined, currentTaskId: currentTaskInfo.uid, tasks: tasks, currentTaskNumber: currentTaskNumber }), _jsx(AdminPreviewGameTask, { taskInfo: currentTaskInfo, selectionTaskAnswers: currentSelectionTaskAnswers, onSelectionTaskAnswerButtonClick: handleSelectionTaskAnswerButtonClick, inputTaskAnswer: currentInputTaskAnswer, onInputTaskAnswerChange: handleInputTaskAnswerChange, uploadPhotoTaskAnswer: currentPhotoUploadTaskAnswer, onUploadPhotoTaskAnswerChange: handlePhotoUploadTaskAnswerChange, onTargetLocationCheckedIn: handleTargetLocationCheckedIn, showTaskResult: showTaskResult, taskAnswerValidationErrors: taskAnswerValidationErrors, currentTaskStatus: currentTaskStatus, currentValidatedInputTaskAttempt: currentValidatedInputTaskAttempt })] }) }), _jsx(PageBottomContent, { children: _jsx(AdminPreviewGameplayControls, { showTaskResult: showTaskResult, currentInputTaskAnswer: currentInputTaskAnswer, currentSelectionTaskAnswers: currentSelectionTaskAnswers, setCurrentSelectionTaskAnswers: setCurrentSelectionTaskAnswers, setCurrentInputTaskAnswer: setCurrentInputTaskAnswer, currentPhotoUploadTaskAnswer: currentPhotoUploadTaskAnswer, currentTaskInfo: currentTaskInfo, setShowTaskResult: setShowTaskResult, goToNextTask: goToNextTask, validateInputField: validateInputField, validateSelect: validateSelect, currentTaskStatus: currentTaskStatus, setCurrentTaskStatus: setCurrentTaskStatus, onTryAgainButtonClick: handleTaskTryAgainButtonClick, previewType: previewType, isLastTask: +currentTaskNumber === tasks.length, currentValidatedInputTaskAttempt: currentValidatedInputTaskAttempt }) })] })) : (_jsx(Loader, {})) }));
};
export default AdminPreviewMode;
